import { ReactNode, FC } from 'react'
import classNames from 'classnames'

import { generateSrcSet } from 'project/image-proxy-web'
import { GooglePlayButton, AppStoreButton } from 'components/AppDownloadButtons'

type RobotImage = {
  src: string
  alt: string
  size?: 'small'
  dimensions: { height: number; width: number }
}

type HeroType = {
  title: string | ReactNode
  titleSize?: 'small' | 'large'
  subtitle?: ReactNode
  robotImage: RobotImage
  description: string | ReactNode
  hideFooter?: boolean
  hideDownloadButtons?: boolean
  logo?: ReactNode
  cta?: ReactNode
  subtitleContainerClassName?: string
}

const HeroBanner: FC<HeroType> = ({
  cta,
  description,
  hideDownloadButtons,
  hideFooter,
  logo,
  robotImage,
  subtitle,
  subtitleContainerClassName,
  title,
  titleSize = 'large',
}) => {
  const {
    alt: imageAlt,
    dimensions: robotImageDimensions,
    size: imageSize,
    src: robotImageSrc,
  } = robotImage

  return (
    <div className='container-fluid ps-4 ps-lg-26 pe-4'>
      <div className='d-flex flex-column flex-lg-row'>
        <div className='d-flex flex-column flex-1 py-8 text-center text-lg-start'>
          <div className='lh-114 me-xxl-n15 me-lg-n6'>
            {logo}
            {typeof title === 'string' ? (
              <h1
                className={classNames('fw-bold mb-lg-3 mb-2 lh-114', {
                  'fs-fluid-34-84': titleSize === 'large',
                  'fs-fluid-36-64': titleSize === 'small',
                })}
              >
                {title}
              </h1>
            ) : (
              title
            )}
            {!!subtitle && (
              <h2 className={subtitleContainerClassName || 'mb-4 mb-lg-3'}>
                {subtitle}
              </h2>
            )}

            <div className='d-flex flex-column align-items-center align-items-lg-start'>
              <span className='fw-semibold fs-fluid-24-26 mb-5 mb-lg-2'>
                {description}
              </span>
              {cta}
              {!hideFooter && (
                <div className='d-none d-lg-block mt-8'>
                  {!hideDownloadButtons && (
                    <div className='mb-xl-8 mb-4'>
                      <div className='d-flex gap-3'>
                        <AppStoreButton />
                        <GooglePlayButton />
                      </div>
                    </div>
                  )}
                  <img
                    loading='eager'
                    fetchPriority='high'
                    className=''
                    {...generateSrcSet(
                      '/images/new/reviews-io-logo.svg',
                      128,
                      46,
                    )}
                    alt='Reviews.io'
                    height={46}
                    width={128}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className={classNames('d-flex flex-column align-items-center', {
            'w-lg-40': imageSize === 'small',
            'w-lg-50': imageSize !== 'small',
          })}
        >
          <img
            {...generateSrcSet(
              robotImageSrc,
              robotImageDimensions.width,
              robotImageDimensions.height,
            )}
            fetchPriority='high'
            loading='eager'
            alt={imageAlt}
            className={'img-fluid'}
            height={robotImageDimensions?.height}
            width={robotImageDimensions?.width}
          />

          <div className='d-flex flex-column align-items-center d-lg-none'>
            {!hideDownloadButtons && (
              <>
                <div className='d-flex gap-3'>
                  <AppStoreButton />
                  <GooglePlayButton />
                </div>
              </>
            )}
            {!hideFooter && (
              <img
                {...generateSrcSet('/images/new/reviews-io-logo.svg', 128, 46)}
                loading='eager'
                fetchPriority='high'
                className='mt-5'
                height={46}
                width={128}
                alt='Reviews.io'
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default HeroBanner
